import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-password-progress-bar',
  templateUrl: './password-progress-bar.component.html',
  styleUrls: ['./password-progress-bar.component.css']
})
export class PasswordProgressBarComponent implements OnInit {

  @Input() set passwordValue(value: string) {
    this._pwdValue = value;
    this.checkComplexityPassword(value);
  }

  @Output() percentageFinal = new EventEmitter<number>();

  private colors = ['#fd0100', '#fea500', '#ffc501', '#fee902', '#f8fd03', '#d6eb14', '#b2d727', '#9ecd33', '#61a720', '#398a12', '#016701'];

  percentage = 0;

  _pwdValue = undefined;

  complexityKey = 'Too Short';

  constructor(private translateService: TranslateService) { }

  ngOnInit() {
  }

  checkBackground(position: number) {
    const umbralMin = position * 10;
    const umbralMax = umbralMin + 10;
    if (this.percentage >= umbralMax) {
      return `linear-gradient(to right, ${this.colors[position]} 0%, ${this.colors[position + 1]} 100%)`;
    }
    if (this.percentage > umbralMin && this.percentage < umbralMax) {
      const percentageMedium = (this.percentage - umbralMin) * 10;
      return `linear-gradient(to right, ${this.colors[position]} -${percentageMedium}%, ` +
                `${this.colors[position + 1]} ${percentageMedium}%, white ${percentageMedium}%)`;
    }
    return '';
  }
  getTextColor() {
    const percentageString = this.percentage.toString();
    let colorPosition = '0';

    if (percentageString.length > 1) {
      colorPosition = percentageString.substr(0, percentageString.length - 1);
    }

    return this.colors[parseInt(colorPosition, 10)];
  }

  private checkComplexityPassword(pwd: string) {
  /*Fórmula sacada de http://www.passwordmeter.com/ */

    // Simultaneous variable declaration and value assignment aren't supported in IE apparently
    // so I'm forced to assign the same value individually per var to support a crappy browser *sigh*
    let nLength = 0, nAlphaUC = 0, nAlphaLC = 0, nNumber = 0, nSymbol = 0, nMidChar = 0, nRequirements = 0;
    let nUnqChar = 0, nRepChar = 0, nRepInc = 0, nConsecAlphaUC = 0;
    let nConsecAlphaLC = 0, nConsecNumber = 0, nSeqAlpha = 0, nSeqNumber = 0, nSeqSymbol = 0;
    let nReqChar = 0;
    const nMultMidChar = 2, nMultConsecAlphaUC = 2, nMultConsecAlphaLC = 2, nMultConsecNumber = 2;
    const nMultSeqAlpha = 3, nMultSeqNumber = 3, nMultSeqSymbol = 3;
    const nMultLength = 4, nMultNumber = 4, nMultSymbol = 6;
    let numberTemporalAlphaUpperCase = 0, nTmpAlphaLC = 0, nTmpNumber = 0;
    const sAlphas = 'abcdefghijklmnopqrstuvwxyz';
    const sNumerics = '01234567890';
    const sSymbols = ')!@#$%^&*()';
    const nMinPwdLen = 8;
    if (!pwd) {
      this.percentage = 0;
      return;
    }
    if (pwd) {
      this.percentage = pwd.length * nMultLength;
      nLength = pwd.length;
      const arrPwd = pwd.replace(/\s+/g, '').split(/\s*/);
      const arrPwdLen = arrPwd.length;

      if (arrPwdLen == 0) {
        this.percentage = 0;
        return;
      }

      /* Loop through password to check for Symbol, Numeric, Lowercase and Uppercase pattern matches */
      for (let a = 0; a < arrPwdLen; a++) {
        if (arrPwd[a].match(/[A-Z]/g)) {
          if ((numberTemporalAlphaUpperCase + 1) == a) {
            nConsecAlphaUC++;
          }
          numberTemporalAlphaUpperCase = a;
          nAlphaUC++;
        } else if (arrPwd[a].match(/[a-z]/g)) {
          if ((nTmpAlphaLC + 1) == a) {
            nConsecAlphaLC++;
          }
          nTmpAlphaLC = a;
          nAlphaLC++;
        } else if (arrPwd[a].match(/[0-9]/g)) {
          if (a > 0 && a < (arrPwdLen - 1)) { nMidChar++; }
          if ((nTmpNumber + 1) == a) {
            nConsecNumber++;
          }
          nTmpNumber = a;
          nNumber++;
        } else if (arrPwd[a].match(/[^a-zA-Z0-9_]/g)) {
          if (a > 0 && a < (arrPwdLen - 1)) {
            nMidChar++;
          }
          nSymbol++;
        }
        /* Internal loop through password to check for repeat characters */
        let bCharExists = false;
        for (let b = 0; b < arrPwdLen; b++) {
          if (arrPwd[a] == arrPwd[b] && a != b) { /* repeat character exists */
            bCharExists = true;
            /*
            Calculate icrement deduction based on proximity to identical characters
            Deduction is incremented each time a new match is discovered
            Deduction amount is based on total password length divided by the
            difference of distance between currently selected match
            */
            nRepInc += Math.abs(arrPwdLen / (b - a));
          }
        }
        if (bCharExists) {
          nRepChar++;
          nUnqChar = arrPwdLen - nRepChar;
          nRepInc = (nUnqChar) ? Math.ceil(nRepInc / nUnqChar) : Math.ceil(nRepInc);
        }
      }

      /* Check for sequential alpha string patterns (forward and reverse) */
      for (let str = 0; str < 23; str++) {
        const sFwd = sAlphas.substring(str, str + 3);
        const sRev = this.strReverse(sFwd);
        if (pwd.toLowerCase().indexOf(sFwd) != -1 || pwd.toLowerCase().indexOf(sRev) != -1) { nSeqAlpha++; }
      }

      /* Check for sequential numeric string patterns (forward and reverse) */
      for (let str = 0; str < 8; str++) {
        const sFwd = sNumerics.substring(str, str + 3);
        const sRev = this.strReverse(sFwd);
        if (pwd.toLowerCase().indexOf(sFwd) != -1 || pwd.toLowerCase().indexOf(sRev) != -1) { nSeqNumber++; }
      }

      /* Check for sequential symbol string patterns (forward and reverse) */
      for (let str = 0; str < 8; str++) {
        const sFwd = sSymbols.substring(str, str + 3);
        const sRev = this.strReverse(sFwd);
        if (pwd.toLowerCase().indexOf(sFwd) != -1 || pwd.toLowerCase().indexOf(sRev) != -1) { nSeqSymbol++; }
      }

      /* Modify overall score value based on usage vs requirements */

      /* General point assignment */
      if (nAlphaUC > 0 && nAlphaUC < nLength) {
        this.percentage = this.percentage + ((nLength - nAlphaUC) * 2);
      }
      if (nAlphaLC > 0 && nAlphaLC < nLength) {
        this.percentage = this.percentage + ((nLength - nAlphaLC) * 2);
      }
      if (nNumber > 0 && nNumber < nLength) {
        this.percentage = this.percentage + (nNumber * nMultNumber);
      }
      if (nSymbol > 0) {
        this.percentage = this.percentage + (nSymbol * nMultSymbol);
      }
      if (nMidChar > 0) {
        this.percentage = this.percentage + (nMidChar * nMultMidChar);
      }

      /* Point deductions for poor practices */
      if ((nAlphaLC > 0 || nAlphaUC > 0) && nSymbol === 0 && nNumber === 0) {  // Only Letters
        this.percentage = this.percentage - nLength;
      }
      if (nAlphaLC === 0 && nAlphaUC === 0 && nSymbol === 0 && nNumber > 0) {  // Only Numbers
        this.percentage = this.percentage - nLength;
      }
      if (nRepChar > 0) {  // Same character exists more than once
        this.percentage = this.percentage - nRepInc;
      }
      if (nConsecAlphaUC > 0) {  // Consecutive Uppercase Letters exist
        this.percentage = this.percentage - (nConsecAlphaUC * nMultConsecAlphaUC);
      }
      if (nConsecAlphaLC > 0) {  // Consecutive Lowercase Letters exist
        this.percentage = this.percentage - (nConsecAlphaLC * nMultConsecAlphaLC);
      }
      if (nConsecNumber > 0) {  // Consecutive Numbers exist
        this.percentage = this.percentage - (nConsecNumber * nMultConsecNumber);
      }
      if (nSeqAlpha > 0) {  // Sequential alpha strings exist (3 characters or more)
        this.percentage = this.percentage - (nSeqAlpha * nMultSeqAlpha);
      }
      if (nSeqNumber > 0) {  // Sequential numeric strings exist (3 characters or more)
        this.percentage = this.percentage - (nSeqNumber * nMultSeqNumber);
      }
      if (nSeqSymbol > 0) {  // Sequential symbol strings exist (3 characters or more)
        this.percentage = this.percentage - (nSeqSymbol * nMultSeqSymbol);
      }

      /* Determine if mandatory requirements have been met and set image indicators accordingly */
      const arrCharsFromCounters = [nLength, nAlphaUC, nAlphaLC, nNumber, nSymbol];
      const arrCharsIds2 = ['nLength', 'nAlphaUC', 'nAlphaLC', 'nNumber', 'nSymbol'];
      for (let numCharacter = 0; numCharacter < arrCharsFromCounters.length; numCharacter++) {
        let minValue;
        if (arrCharsIds2[numCharacter] == 'nLength') {
          minValue = nMinPwdLen - 1;
        } else {
          minValue = 0;
        }
        if (arrCharsFromCounters[numCharacter] == minValue + 1) {
          nReqChar++;
        } else if (arrCharsFromCounters[numCharacter] > minValue + 1) {
          nReqChar++;
        }
      }
      nRequirements = nReqChar;
      let nMinReqCharacters;
      if (pwd.length >= nMinPwdLen) {
        nMinReqCharacters = 3;
      } else {
        nMinReqCharacters = 4;
      }
      if (nRequirements > nMinReqCharacters) {  // One or more required characters exist
        this.percentage = this.percentage + (nRequirements * 2);
      }



      /* Determine complexity based on overall score */
      if (this.percentage > 100) {
        this.percentage = 100;
      } else if (this.percentage < 0) {
        this.percentage = 0;
      }

      if (this.percentage >= 0 && this.percentage < 20) {
        this.complexityKey = this.translateService.instant('mensajes.passwordSecurity.veryWeak');
      } else if (this.percentage >= 20 && this.percentage < 40) {
        this.complexityKey = this.translateService.instant('mensajes.passwordSecurity.weak');
      } else if (this.percentage >= 40 && this.percentage < 60) {
        this.complexityKey = this.translateService.instant('mensajes.passwordSecurity.good');
      } else if (this.percentage >= 60 && this.percentage < 80) {
        this.complexityKey = this.translateService.instant('mensajes.passwordSecurity.strong');
      } else if (this.percentage >= 80 && this.percentage <= 100) {
        this.complexityKey = this.translateService.instant('mensajes.passwordSecurity.veryStrong');
      }

      this.percentageFinal.emit(this.percentage);
    }
  }

  private strReverse(sFwd: string) {
    let newstring = '';
    for (let str = 0; str < sFwd.length; str++) {
      newstring = sFwd.charAt(str) + newstring;
    }
    return newstring;
  }

}
